import { buildReorderPlan, dollarsToMinor, getLocationDisplayName, itemizeLine, PER_UNIT_SUFFIX, type ReorderLine } from '@order-up/common';
import { createClientLogger, useAuth, useCart, useGetItemsByRestaurantId, useGetMenuGroupsByRestaurantId, useGetOrderByGuid, useGetLocationBySlug, useOrderPaymentSummary, useTimeFormatter, type CustomerPaymentSummary } from '@order-up/client/vite';
import { isTerminalCourierPhase, mapRawCourierStatusToPhase, Order, resolvePrepLeadMinutes, type OrderItem } from '@order-up/models';
import { Check, Clock, CreditCard, MapPin, Package, ShoppingBag } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import OrderStatusTracker from '../components/OrderStatusTracker';
import LazyOnlineServiceFeeRow from '../components/LazyOnlineServiceFeeRow';
import { splitConfirmationTotals } from '../utils/confirmationTotals';
import { useMenuUrl } from '../lib/menuUrl';

/** Route state passed from CheckoutPage after successful payment */
interface CheckoutRouteState {
    transactionRef?: string;
    amount?: string;
    paidAt?: string; // ISO timestamp of payment processing
    accessToken?: string; // durable order-access token (also mirrored in the URL `?t=`)
}

// Logger for order confirmation page
const logger = createClientLogger('OrderConfirmationPage');

/**
 * Should this browser be bounced off the confirmation page back to the menu?
 *
 * 🔴 THE `?t=` CAPABILITY AUTHORIZED THE API AND NOT THE UI. The receipt link
 * carries a server-signed, order-scoped access token whose entire stated
 * purpose is that "the receipt link keeps working across devices/sessions
 * without depending on the ephemeral uid" (see `orderAccessToken.ts`). The
 * page then compared `order.customerId` to the live Firebase uid anyway and,
 * on a mismatch, navigated to `/{slug}` — which renders `CheckoutPage`, i.e.
 * a generic checkout with no cart in it. That is how a customer clicking
 * "Track your order" in their own receipt email lands on an empty checkout.
 *
 * The comparison is not merely unhelpful, it is REDUNDANT: `order` is only in
 * hand because `GET /orders/by-guid/:guid` returned it, and that route runs
 * `callerMayAccessOrder` — OWNER ∨ STAFF ∨ a valid token — and 403s otherwise.
 * The server has therefore already decided. Re-deciding on the client with a
 * narrower rule can only ever take access away from someone the server
 * allowed.
 *
 * So the token wins outright. With no token the old owner check is kept
 * verbatim: it is a cheap belt-and-braces for the ordinary same-session path,
 * and removing it would be a change nobody asked for.
 *
 * Pure and exported so it is testable without mounting the page.
 */
export function shouldLeaveConfirmationPage(params: {
    /** A `?t=` order-access token was presented (its validity is the server's call). */
    hasAccessToken: boolean;
    /** `customerId` on the loaded order, when the order has loaded at all. */
    orderCustomerId?: string;
    /** Live Firebase uid, when one is signed in. */
    authUid?: string;
}): boolean {
    // The capability is the credential. The server already honoured it.
    if (params.hasAccessToken) return false;
    // Nothing loaded yet, or nobody signed in — not this gate's call.
    if (!params.orderCustomerId || !params.authUid) return false;
    return params.orderCustomerId !== params.authUid;
}

/**
 * Human label for one tender on the confirmation receipt. Cards show the
 * canonical brand + last4 (`Visa ···· 4242`); other methods show a friendly
 * label (`Gift card ···· 5678`, `Cash`, `Check`). Wallets (Apple/Google Pay)
 * arrive as credit/debit carrying the network-token brand + last4, so they
 * render as a card row — honest and sufficient for a receipt.
 */
function tenderLabel(p: CustomerPaymentSummary): string {
    const last4 = p.cardLast4 ? ` ···· ${p.cardLast4}` : '';
    switch (p.paymentMethod) {
        case 'credit':
        case 'debit':
            return `${p.cardBrand || 'Card'}${last4}`;
        case 'gift_card':
            return `Gift card${last4}`;
        case 'cash':
            return 'Cash';
        case 'check':
            return 'Check';
        case 'house_account':
            return 'House account';
        case 'comp':
            return 'Comp';
        default:
            return p.cardBrand ? `${p.cardBrand}${last4}` : last4 ? `Card${last4}` : 'Payment';
    }
}

export default function OrderConfirmationPage() {
    const { slug, orderGuid } = useParams<{ slug: string; orderGuid: string }>();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const checkoutState = (location.state as CheckoutRouteState) || {};
    const { data: authData } = useAuth();
    const { authUser } = authData || {};

    // Durable order-access token: prefer the URL (survives refresh / bookmark /
    // different device), fall back to route state from the just-completed checkout.
    const orderAccessToken = searchParams.get('t') ?? checkoutState.accessToken ?? undefined;

    // Load restaurant to get tenant/restaurant doc IDs for order query
    const { data: locationData, isLoading: isLoadingLocation } = useGetLocationBySlug(slug || '');
    // The menu page the guest ordered from, its custom domain included. Every
    // "leave this page" path goes there with a full navigation: this app's own
    // `/{slug}` is the CHECKOUT page, which with no `?cart=` only says
    // "Cart Missing".
    const menuUrl = useMenuUrl(slug ?? '', locationData?.primaryDomain);
    const locationRecord = locationData?.restaurant;
    const tenant = locationData?.tenant;

    // Public apps have no SelectionProvider, so pass the resolved restaurant's
    // settings directly for accurate per-restaurant clock/date formatting.
    const { formatTime, formatDateTime } = useTimeFormatter(locationRecord?.settings);

    // Fetch order from database using GUID. Poll every 20s for live status while
    // the order is in progress; stop once it reaches a terminal state so we don't
    // poll forever on a completed/cancelled order.
    const { data: order, isLoading: isLoadingOrder, error: orderError } = useGetOrderByGuid(
        orderGuid || '',
        tenant?.tenantId || 0,
        locationRecord?.restaurantId || 0,
        {
            accessToken: orderAccessToken,
            refetchInterval: (query) => {
                const o = query?.state?.data as { orderState?: string; courierStatus?: string } | null | undefined;
                if (!o) return 20000;
                // `abandoned`: a checkout that never paid — nothing more will happen to it.
                const terminalState = ['closed', 'cancelled', 'voided', 'transferred', 'abandoned'].includes(o.orderState ?? '');
                const terminalCourier = isTerminalCourierPhase(mapRawCourierStatusToPhase(o.courierStatus));
                return terminalState || terminalCourier ? false : 20000;
            },
        }
    );

    // Authoritative, refresh-proof payment details. A checkout customer cannot
    // read PaymentRecords from Firestore (rules require staff claims) nor the
    // staff /payments/by-order route — so the confirmation used to lose the
    // charge/tip and the card brand/last4 on refresh. This token-gated,
    // PCI-safe route returns them (gated identically to the order-by-guid load;
    // the same access token authorizes it). Only display fields — no PAN /
    // maskedPan / cardholder name / processor refs / tokens.
    const { data: paymentSummary } = useOrderPaymentSummary(orderGuid, {
        tenantId: tenant?.tenantId || 0,
        restaurantId: locationRecord?.restaurantId || 0,
        accessToken: orderAccessToken,
    });

    // ── Reorder ──────────────────────────────────────────────────────────────
    // The live menu, because a repeat is priced by the menu as it is NOW. The
    // old order's prices are shown only where they changed; putting one in a
    // cart line would show the guest one number and have the server charge
    // another at checkout.
    const { data: menuItems } = useGetItemsByRestaurantId(
        tenant?.tenantId || 0,
        locationRecord?.restaurantId || 0,
    );
    const { data: menuGroups } = useGetMenuGroupsByRestaurantId(
        tenant?.tenantId || 0,
        locationRecord?.restaurantId || 0,
    );

    const reorderCart = useCart({
        restaurantId: locationRecord?.restaurantId || 0,
        tenantId: tenant?.tenantId || 0,
        tenantDocId: tenant?.docId || '',
        restaurantDocId: locationRecord?.docId || '',
    });

    // Hoisted out of the memo's dependency list: the React Compiler cannot track
    // an optional chain there, and it is the same value either way.
    const orderItems = order?.items;

    const reorderPlan = useMemo(() => {
        if (!orderItems?.length || !menuItems?.length || !menuGroups?.length) return null;
        return buildReorderPlan(
            orderItems.map((item: OrderItem) => ({
                menuItemId: item.menuItemId,
                name: item.name,
                quantity: item.quantity,
                unitPrice: item.price,
                menuGroupId: item.menuGroupId,
            })),
            menuItems,
            menuGroups,
            new Date(),
            // The RESTAURANT's zone. Menu schedules are its wall clock, so
            // omitting this judged them against the guest's phone.
            tenant?.settings?.timezone ?? locationRecord?.settings?.timezone,
        );
    }, [orderItems, menuItems, menuGroups, tenant, locationRecord]);

    const [isReordering, setIsReordering] = useState(false);

    /**
     * Put the repeatable lines in the cart and go to the menu.
     *
     * Anything the plan could not repeat cleanly — gone, or needing the guest
     * to choose a size or a menu — is deliberately NOT added and is listed
     * above the button instead. Silently dropping a line is how a guest pays
     * for an order they did not mean to place.
     */
    const handleReorder = useCallback(async () => {
        if (!reorderPlan || isReordering) return;
        setIsReordering(true);
        try {
            for (const line of reorderPlan.addable) {
                await reorderCart.addItem({
                    menuItemId: line.menuItemId,
                    menuGroupId: line.menuGroupId,
                    name: line.name,
                    quantity: line.quantity,
                    // The price resolved from the CURRENT menu, never the one
                    // on the old order.
                    price: line.currentUnitPrice ?? 0,
                });
            }
        } catch (err) {
            logger.error('Reorder failed to build the cart', err);
        } finally {
            setIsReordering(false);
            // The reordered cart lives in Firestore under this guest, so the
            // menu page loads it.
            window.location.assign(menuUrl);
        }
    }, [reorderPlan, isReordering, reorderCart, menuUrl]);

    // Reduce the PaymentRecords to what the confirmation shows: the net amount
    // charged (refund children net out), the tip actually paid, any refunded
    // total, and the tenders to list. Failed/void/pending records are excluded
    // so they never move the displayed charge.
    const paymentBreakdown = useMemo(() => {
        if (!paymentSummary || paymentSummary.length === 0) return null;
        // Statuses that represent NO real money movement — excluded from the sum.
        const NON_SETTLING = new Set([
            'pending', 'declined', 'error', 'voided',
            'offlineDeclinedSoft', 'offlineRecovering', 'offlineUnrecoverable',
        ]);
        const active = paymentSummary.filter((p) => !NON_SETTLING.has(p.status));
        // Net charged = signed sum: refund children carry a NEGATIVE `amount` and
        // net themselves out against their parent sale.
        const charged = active.reduce((s, p) => s + (p.amount || 0), 0);
        // Tip = non-refund tip only (a refund child's negative tip must not reduce
        // the tip the customer is shown they paid).
        const tip = active
            .filter((p) => !p.isRefund)
            .reduce((s, p) => s + (p.tipAmount || 0), 0);
        const refundTotal = active
            .filter((p) => p.isRefund)
            .reduce((s, p) => s + Math.abs(p.amount || 0), 0);
        // Tenders to list on the receipt = the successful, non-refund charges.
        const tenders = active.filter((p) => !p.isRefund);
        const round = (n: number) => Math.round(n * 100) / 100;
        return {
            charged: round(charged),
            tip: round(tip),
            refundTotal: round(refundTotal),
            tenders,
            // Newest actual processing time, when the record carries one.
            processedAt: tenders.find((t) => t.processedAt)?.processedAt ?? null,
        };
    }, [paymentSummary]);

    // Payment details fall back to route state (transactionRef, amount, paidAt)
    // passed by CheckoutPage on the happy path when the authoritative summary is
    // not yet loaded / genuinely unavailable (e.g. no access token).

    // Apply restaurant branding via CSS custom properties
    useEffect(() => {
        if (locationRecord?.websiteConfig?.branding) {
            const { colors } = locationRecord.websiteConfig.branding;

            document.documentElement.style.setProperty('--brand-primary', colors.primary);
            document.documentElement.style.setProperty('--brand-secondary', colors.secondary);
            document.documentElement.style.setProperty('--brand-accent', colors.accent);

            document.title = `Order Confirmation - ${getLocationDisplayName(locationRecord)} | OrderUp`;
        }
    }, [locationRecord]);

    // Security: Verify user has permission to view this order
    useEffect(() => {
        if (!orderGuid) {
            logger.warn('No order GUID in URL, redirecting to menu');
            window.location.replace(menuUrl);
            return;
        }

        // Once the order is loaded, verify ownership — unless the visitor came
        // in on the receipt link's own access token, which the server has
        // already honoured. See shouldLeaveConfirmationPage.
        if (
            shouldLeaveConfirmationPage({
                hasAccessToken: Boolean(orderAccessToken),
                orderCustomerId: order?.customerId,
                authUid: authUser?.uid,
            })
        ) {
            logger.error('Order does not belong to current user, access denied');
            window.location.replace(menuUrl);
        }
    }, [orderGuid, order, authUser, menuUrl, orderAccessToken]);

    // Get restaurant address for display
    const fullAddress = useMemo(() => {
        if (!locationRecord?.address) return '';
        const { street, city, state, zipCode } = locationRecord.address;
        return `${street}, ${city}, ${state} ${zipCode}`;
    }, [locationRecord]);

    // Customer-facing view of the order. The by-GUID API returns Order.toJSON(),
    // which OMITS the computed getters (subtotal/total/tax) and uses `type`
    // (dineIn/takeOut/delivery/quickService) — NOT a legacy `orderType`. We
    // rehydrate the model so money is faithful and map the real fulfillment type.
    const view = useMemo(() => {
        if (!order) return null;
        let o: Order | null = null;
        try {
            o = Order.fromJSON(order);
        } catch {
            o = null;
        }
        const type: string | undefined = o?.type ?? order.type;
        const isDelivery = type === 'delivery';

        const subtotal = o?.subtotal ?? order.subtotal ?? 0;
        const goodsTotal = o?.total ?? subtotal; // subtotal + tax + service charges/tax
        // The Online Service Fee has its own row; only its TAX stays in "Tax & Fees".
        const onlineServiceFee = o?.onlineServiceFee ?? order.onlineServiceFee ?? null;
        const { taxAndFees, onlineServiceFeeAmount } = splitConfirmationTotals({ subtotal, goodsTotal, onlineServiceFee });
        const deliveryFee = (order.customerDeliveryFeeCents ?? 0) / 100;

        // The tip lives on the Check(s), NOT the Order — `order.checks` holds
        // check GUIDs, not embedded objects, so it can't be summed here — and a
        // customer on this page CANNOT read PaymentRecords (Firestore rules
        // require restaurant-staff claims; the /payments/by-order API is
        // staff-JWT-only). So the ONLY authoritative charge/tip source is the
        // checkout response passed via route state (`checkoutState.amount`), which
        // is NOT mirrored to the URL and is therefore LOST on refresh / bookmark /
        // a second device.
        //
        // When present, `charged` is the exact amount the card saw and `tip` is
        // its residual over goods + delivery. When absent, the tip is genuinely
        // unknowable — so we mark the charge UNKNOWN (null) and the UI omits the
        // Amount-Charged / Total rather than render a Total that silently DROPS
        // the tip (understating the real charge). We never show a number lower
        // than the real charge.
        const chargedRaw = checkoutState.amount != null ? Number(checkoutState.amount) : NaN;
        const chargeKnown = Number.isFinite(chargedRaw);
        const charged: number | null = chargeKnown ? chargedRaw : null;
        const tip: number | null = chargeKnown
            ? Math.max(0, Math.round((chargedRaw - goodsTotal - deliveryFee) * 100) / 100)
            : null;

        return {
            type,
            isDelivery,
            subtotal,
            taxAndFees,
            onlineServiceFee: onlineServiceFee && onlineServiceFeeAmount > 0 ? onlineServiceFee : null,
            deliveryFee,
            tip,
            charged,
            scheduledTime:
                o?.scheduledTime ??
                (order.scheduledTime ? new Date(order.scheduledTime) : undefined),
            createdAt:
                o?.createdAt ?? (order.createdAt ? new Date(order.createdAt) : undefined),
            eta: order.eta as string | undefined,
            // The kitchen's persisted promise (dynamic prep time) — the time the
            // order autopilot will mark it ready, so the page says the same.
            promisedReadyAt:
                o?.promisedReadyAt ??
                (order.promisedReadyAt ? new Date(order.promisedReadyAt) : undefined),
            kitchenState: order.kitchenState as string | undefined,
            deliveryAddressComponents: order.deliveryAddressComponents as
                | { line1?: string; line2?: string; city?: string; state?: string; postalCode?: string }
                | undefined,
        };
    }, [order, checkoutState.amount]);

    // Estimated ready/delivery time — driven by the order's REAL signals
    // (scheduled time, kitchen "ready", courier ETA) instead of a hardcoded ASAP.
    const DELIVERY_BUFFER_MIN = 20;
    const formatPickupTime = useMemo(() => {
        if (!view) return { time: '', subtitle: '' };
        const isDelivery = view.isDelivery;

        // Scheduled order: show the exact time the customer selected.
        if (view.scheduledTime) {
            const date = new Date(view.scheduledTime);
            const now = new Date();
            const isToday = date.toDateString() === now.toDateString();
            const isTomorrow =
                date.toDateString() ===
                new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();
            const timeString = formatTime(date);
            if (isToday) return { time: `Today at ${timeString}`, subtitle: 'Scheduled order' };
            if (isTomorrow) return { time: `Tomorrow at ${timeString}`, subtitle: 'Scheduled order' };
            const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return { time: `${dateString} at ${timeString}`, subtitle: 'Scheduled order' };
        }

        // ASAP: surface the live kitchen/courier signal when we have one.
        if (view.kitchenState === 'ready' || view.kitchenState === 'served') {
            return {
                time: isDelivery ? 'Ready for delivery' : 'Ready for pickup',
                subtitle: 'Your order is ready now',
            };
        }
        if (isDelivery && view.eta) {
            const etaDate = new Date(view.eta);
            if (!isNaN(etaDate.getTime())) {
                return { time: `Arriving ~ ${formatTime(etaDate)}`, subtitle: 'Estimated delivery time' };
            }
        }

        const preparing = view.kitchenState === 'preparing' || view.kitchenState === 'sent';
        // The order's own promise wins: it is what the kitchen committed to and
        // when the autopilot marks it ready — paced when the kitchen is busy.
        if (view.promisedReadyAt && !isNaN(view.promisedReadyAt.getTime())) {
            const ready = new Date(
                view.promisedReadyAt.getTime() + (isDelivery ? DELIVERY_BUFFER_MIN * 60000 : 0),
            );
            return {
                time: `${preparing ? 'Being prepared · ' : ''}${isDelivery ? 'Arriving' : 'Ready'} ~ ${formatTime(ready)}`,
                subtitle: isDelivery ? 'Estimated delivery time' : 'Estimated ready time',
            };
        }

        // Otherwise (an order from before promises were saved), estimate from
        // the restaurant's ONE standard prep time (+ a delivery buffer).
        const prepTimeMinutes = resolvePrepLeadMinutes(locationRecord);
        const est = prepTimeMinutes + (isDelivery ? DELIVERY_BUFFER_MIN : 0);
        const buffer = Math.ceil(est * 0.25);
        const orderTime = view.createdAt ? new Date(view.createdAt) : new Date();
        const ready = new Date(orderTime.getTime() + est * 60000);
        return {
            time: `${preparing ? 'Being prepared · ' : ''}${isDelivery ? 'Arriving' : 'Ready'} ~ ${formatTime(ready)}`,
            subtitle: `Approx. ${est - buffer}–${est + buffer} minutes`,
        };
    }, [view, locationRecord, formatTime]);

    // Loading state — spinner-free skeleton that mirrors the confirmation card.
    if (isLoadingLocation || isLoadingOrder) {
        return (
            <div className="min-h-svh bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
                    <div className="w-16 h-16 rounded-full bg-gray-200 motion-safe:animate-pulse mx-auto mb-6" />
                    <div className="h-6 w-48 rounded bg-gray-200 motion-safe:animate-pulse mx-auto mb-3" />
                    <div className="h-4 w-64 rounded bg-gray-200 motion-safe:animate-pulse mx-auto mb-8" />
                    <div className="space-y-3">
                        <div className="h-4 w-full rounded bg-gray-200 motion-safe:animate-pulse" />
                        <div className="h-4 w-5/6 rounded bg-gray-200 motion-safe:animate-pulse" />
                        <div className="h-4 w-2/3 rounded bg-gray-200 motion-safe:animate-pulse" />
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (orderError || !order) {
        return (
            <div className="min-h-svh bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
                    <p className="text-gray-600 mb-6">
                        We couldn't find this order. It may have been cancelled or the link may be incorrect.
                    </p>
                    <a
                        href={menuUrl}
                        className="block w-full text-center bg-linear-to-r from-gray-700 to-gray-800 text-white font-semibold py-3 rounded-xl hover:from-gray-800 hover:to-gray-900 transition-all"
                    >
                        {locationRecord ? `Back to ${getLocationDisplayName(locationRecord)}` : 'Return to Menu'}
                    </a>
                </div>
            </div>
        );
    }

    // `order` is guaranteed here (error state returned above), so `view` is set.
    if (!view) return null;

    // Prefer the authoritative, refresh-proof charge/tip from PaymentRecords once
    // loaded; fall back to the instant checkout-response value on the happy path
    // (route state), and finally to the "see email receipt" copy when neither is
    // available (token missing — with the token the fetch succeeds).
    const displayCharged: number | null = paymentBreakdown ? paymentBreakdown.charged : view.charged;
    const displayTip: number | null = paymentBreakdown ? paymentBreakdown.tip : view.tip;
    const displayRefund: number = paymentBreakdown?.refundTotal ?? 0;
    const tenders = paymentBreakdown?.tenders ?? [];

    const brandColor = locationRecord?.websiteConfig?.branding?.colors?.primary || '#22C55E';

    // Derive the payment/order status from the live order doc instead of
    // hardcoding "Approved" — a voided or cancelled order must NOT keep showing
    // a green "Approved" badge when the page is revisited.
    const paymentDisplay = (() => {
        switch (order.orderState) {
            case 'voided':
                return { header: 'Payment Voided', label: 'Voided', tone: 'bg-red-100 text-red-800', ok: false };
            case 'cancelled':
                return { header: 'Order Cancelled', label: 'Cancelled', tone: 'bg-red-100 text-red-800', ok: false };
            // Persist-first checkout: the order exists before its payment is
            // confirmed. Never show "Approved" for money that is not in.
            case 'awaitingPayment':
                return { header: 'Confirming Payment', label: 'Processing', tone: 'bg-amber-100 text-amber-800', ok: false };
            case 'abandoned':
                return { header: 'Payment Not Completed', label: 'Not charged', tone: 'bg-gray-100 text-gray-800', ok: false };
            default:
                return { header: 'Payment Confirmed', label: 'Approved', tone: 'bg-green-100 text-green-800', ok: true };
        }
    })();

    // Header title/subtitle keyed on the REAL order state — "Order Confirmed!"
    // is a lie for an order the restaurant hasn't accepted yet (it can still be
    // denied) or one scheduled for later. The payment IS captured in all these
    // cases (paymentDisplay above stays "Approved"); it's the ORDER's status the
    // header must tell the truth about.
    const locationName = locationRecord ? getLocationDisplayName(locationRecord) : 'the restaurant';
    const headerCopy = (() => {
        switch (order.orderState) {
            case 'voided':
                return { title: 'Order voided', subtitle: `Your payment was voided — please contact ${locationName}.` };
            case 'cancelled':
                return { title: 'Order cancelled', subtitle: `If you believe this is a mistake, please contact ${locationName}.` };
            case 'awaitingPayment':
                return {
                    title: 'Finishing your order',
                    subtitle: `We're confirming your payment with ${locationName}. This page updates on its own.`,
                };
            case 'abandoned':
                return {
                    title: 'Checkout not completed',
                    subtitle: `This order was never paid for, so nothing was placed. You can start a new order with ${locationName}.`,
                };
            case 'pendingAcceptance':
            case 'draft':
                return {
                    title: 'Order received',
                    subtitle: `We've got your order and payment — waiting for ${locationName} to confirm it.`,
                };
            case 'scheduled':
                return {
                    title: 'Order scheduled',
                    subtitle: `Your order is set with ${locationName} — we'll have it ready around your scheduled time.`,
                };
            default:
                return { title: 'Order confirmed!', subtitle: `Thank you for your order at ${getLocationDisplayName(locationRecord)}` };
        }
    })();

    return (
        <div className="min-h-svh bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Success Card */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
                    {/* Header with Check Icon */}
                    <div className="p-8 text-center" style={{
                        background: `linear-gradient(135deg, ${brandColor}dd, ${brandColor})`
                    }}>
                        <div className="flex justify-center mb-4">
                            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                                <Check className="w-12 h-12" style={{ color: brandColor }} strokeWidth={3} />
                            </div>
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{headerCopy.title}</h1>
                        <p className="text-white/90 text-lg">
                            {headerCopy.subtitle}
                        </p>
                    </div>

                    {/* Order Details */}
                    <div className="p-6 sm:p-8 space-y-6">
                        {/* Order Number */}
                        <div className="text-center pb-6 border-b border-gray-200">
                            <p className="text-sm text-gray-600 mb-1">Order Number</p>
                            <p className="text-4xl font-bold text-gray-900">#{order.orderNumber ?? order.orderId}</p>
                            {checkoutState.transactionRef && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Transaction ID: {checkoutState.transactionRef}
                                </p>
                            )}
                        </div>

                        {/* Order Info Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Order Type */}
                            <div className="bg-linear-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 border border-blue-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                                        {view.isDelivery ? (
                                            <Package className="w-6 h-6 text-blue-600" />
                                        ) : (
                                            <ShoppingBag className="w-6 h-6 text-blue-600" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-blue-700 font-medium uppercase tracking-wide">Order Type</p>
                                        <p className="text-base font-bold text-blue-900">
                                            {view.isDelivery ? 'Delivery' : 'Pickup'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Pickup Time */}
                            <div className="bg-linear-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 border border-purple-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                                        <Clock className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-purple-700 font-medium uppercase tracking-wide">
                                            {view.isDelivery ? 'Delivery Time' : 'Pickup Time'}
                                        </p>
                                        <p className="text-base font-bold text-purple-900">
                                            {formatPickupTime.time}
                                        </p>
                                        {formatPickupTime.subtitle && (
                                            <p className="text-xs text-purple-600 mt-0.5">
                                                {formatPickupTime.subtitle}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Restaurant Address (for pickup) */}
                        {!view.isDelivery && fullAddress && (
                            <div className="bg-linear-to-br from-amber-50 to-amber-100/50 rounded-xl p-4 border border-amber-200">
                                <div className="flex items-start gap-3">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                                        <MapPin className="w-6 h-6 text-amber-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-amber-700 font-medium uppercase tracking-wide mb-1">Pickup Location</p>
                                        <p className="text-sm font-semibold text-amber-900">{getLocationDisplayName(locationRecord)}</p>
                                        <p className="text-sm text-amber-800">{fullAddress}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Delivery Address */}
                        {view.isDelivery && view.deliveryAddressComponents && (
                            <div className="bg-linear-to-br from-amber-50 to-amber-100/50 rounded-xl p-4 border border-amber-200">
                                <div className="flex items-start gap-3">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                                        <MapPin className="w-6 h-6 text-amber-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-amber-700 font-medium uppercase tracking-wide mb-1">Delivery Address</p>
                                        <p className="text-sm text-amber-900">
                                            {view.deliveryAddressComponents.line1}
                                            {view.deliveryAddressComponents.line2 && `, ${view.deliveryAddressComponents.line2}`}
                                        </p>
                                        <p className="text-sm text-amber-800">
                                            {view.deliveryAddressComponents.city}, {view.deliveryAddressComponents.state} {view.deliveryAddressComponents.postalCode}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Order Items */}
                {order.items && order.items.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Order</h2>
                        <div className="space-y-4">
                            {order.items.map((item: OrderItem, index: number) => {
                                // Calculate total price for this item (quantity * (price + modifiers))
                                const modifiersTotal = item.modifierGroups.reduce((sum, group) =>
                                    sum + group.modifiers.reduce((groupSum, mod) => groupSum + mod.priceDelta, 0), 0
                                );
                                const itemTotalPrice = (item.price + modifiersTotal) * item.quantity;
                                // Base price up top and a closing "Item total" when the
                                // line has priced add-ons — `itemizeLine`, the rule
                                // the till, the receipt and the back office follow.
                                // The check counts the amounts THIS page shows
                                // (upcharges only), so a line with a hidden
                                // deduction keeps its single price.
                                const itemized = itemizeLine({
                                    quantity: item.quantity,
                                    unitMinor: dollarsToMinor(item.price),
                                    modifierMinor: item.modifierGroups.flatMap((group) =>
                                        group.modifiers
                                            .filter((mod) => mod.priceDelta > 0)
                                            .map((mod) => dollarsToMinor(mod.priceDelta)),
                                    ),
                                    totalMinor: dollarsToMinor(itemTotalPrice),
                                });

                                return (
                                    <div key={index} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2 mb-1">
                                                <h3 className="font-semibold text-gray-900">
                                                    {item.quantity > 1 && `${item.quantity}× `}
                                                    {item.name}
                                                </h3>
                                                <span className="font-bold text-gray-900 whitespace-nowrap">
                                                    {itemized
                                                        ? `$${item.price.toFixed(2)}${itemized.perUnit ? PER_UNIT_SUFFIX : ''}`
                                                        : `$${itemTotalPrice.toFixed(2)}`}
                                                </span>
                                            </div>

                                            {/* Modifiers */}
                                            {item.modifierGroups && item.modifierGroups.length > 0 && (
                                                <div className="text-sm space-y-0.5 mb-1">
                                                    {item.modifierGroups.map((group, groupIdx) =>
                                                        group.modifiers.map((mod, modIdx) => (
                                                            <div key={`${groupIdx}-${modIdx}`} className="flex items-center justify-between text-gray-600">
                                                                <span>
                                                                    {mod.priceDelta > 0 ? '+ ' : ''}
                                                                    {mod.name}
                                                                </span>
                                                                {mod.priceDelta > 0 && (
                                                                    <span>+${mod.priceDelta.toFixed(2)}</span>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                    {itemized && (
                                                        <div
                                                            className="flex items-center justify-between border-t border-gray-100 pt-0.5 font-semibold text-gray-900"
                                                            data-testid="item-total"
                                                        >
                                                            <span>{itemized.totalLabel}</span>
                                                            <span>${itemTotalPrice.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Notes */}
                                            {item.notes && (
                                                <p className="text-sm text-gray-600 italic mt-1">Note: {item.notes}</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Order Summary */}
                        <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                            <div className="flex items-center justify-between text-base">
                                <span className="text-gray-700">Subtotal</span>
                                <span className="font-semibold text-gray-900">${view.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between text-base">
                                <span className="text-gray-700">Tax &amp; Fees</span>
                                <span className="font-semibold text-gray-900">${view.taxAndFees.toFixed(2)}</span>
                            </div>
                            {view.onlineServiceFee && (
                                <LazyOnlineServiceFeeRow fee={view.onlineServiceFee} locationName={locationName} />
                            )}
                            {view.isDelivery && view.deliveryFee > 0 && (
                                <div className="flex items-center justify-between text-base">
                                    <span className="text-gray-700">Delivery fee</span>
                                    <span className="font-semibold text-gray-900">${view.deliveryFee.toFixed(2)}</span>
                                </div>
                            )}
                            {displayTip != null && displayTip > 0 && (
                                <div className="flex items-center justify-between text-base">
                                    <span className="text-gray-700">Tip</span>
                                    <span className="font-semibold text-gray-900">${displayTip.toFixed(2)}</span>
                                </div>
                            )}
                            {displayRefund > 0 && (
                                <div className="flex items-center justify-between text-base">
                                    <span className="text-gray-700">Refunded</span>
                                    <span className="font-semibold text-red-700">-${displayRefund.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex items-center justify-between text-xl pt-3 border-t border-gray-200">
                                <span className="font-bold text-gray-900">Total</span>
                                {displayCharged != null ? (
                                    <span className="font-bold text-gray-900">${displayCharged.toFixed(2)}</span>
                                ) : (
                                    // Charge unknown on refresh (tip can't be reconstructed) —
                                    // point to the receipt instead of understating the Total.
                                    <span className="text-sm font-medium text-gray-500">See email receipt</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Live order status — polls the order while in progress */}
                <OrderStatusTracker order={order} brandColor={brandColor} />

                {/* Payment Confirmation */}
                <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${brandColor}20` }}>
                            <CreditCard className="w-6 h-6" style={{ color: brandColor }} strokeWidth={2.5} />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-3">
                                {paymentDisplay.header}
                            </h3>

                            {/* Payment Details — authoritative once the token-gated payment
                                summary loads (survives refresh / bookmark / second device);
                                otherwise falls back to the checkout response (route state). */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-3 space-y-3">
                                {/* Amount Charged. `displayCharged` prefers the summed
                                    PaymentRecords (refunds netted, tip included) and falls back
                                    to the checkout response; only when BOTH are unavailable
                                    (token missing) do we point to the emailed receipt rather
                                    than show a total that understates the real charge. */}
                                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                                    <span className="font-bold text-gray-900">Amount Charged</span>
                                    {displayCharged != null ? (
                                        <span className="font-bold text-xl text-gray-900">
                                            ${displayCharged.toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-sm text-gray-500 text-right max-w-[60%]">
                                            Charged in full — see your emailed receipt for the total
                                        </span>
                                    )}
                                </div>

                                {/* Payment method(s) — one row per tender (card brand · last4,
                                    or the method label for cash / gift / wallet). Handles split
                                    tenders; refund children are excluded (netted into the total
                                    and surfaced as the "Refunded" line above). */}
                                {tenders.length > 0 && (
                                    <div className="flex items-start justify-between text-sm gap-3">
                                        <span className="text-gray-600 shrink-0">
                                            {tenders.length > 1 ? 'Payment methods' : 'Payment method'}
                                        </span>
                                        <div className="flex flex-col items-end gap-1 text-right">
                                            {tenders.map((t, i) => (
                                                <span key={i} className="inline-flex items-center gap-1.5 font-medium text-gray-900">
                                                    <CreditCard className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />
                                                    {tenderLabel(t)}
                                                    {tenders.length > 1 && (
                                                        <span className="text-gray-500 font-normal">
                                                            ${Math.abs(t.amount).toFixed(2)}
                                                        </span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Transaction Status */}
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">Status</span>
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${paymentDisplay.tone}`}>
                                        {paymentDisplay.ok && <Check className="w-3 h-3" strokeWidth={3} />}
                                        {paymentDisplay.label}
                                    </span>
                                </div>

                                {/* Payment Date — prefer the PaymentRecord's actual processed
                                    time, then the checkout response, then order createdAt. */}
                                {(paymentBreakdown?.processedAt || checkoutState.paidAt || order.createdAt) && (
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Processed</span>
                                        <span className="font-medium text-gray-900">
                                            {formatDateTime(new Date(paymentBreakdown?.processedAt || checkoutState.paidAt || order.createdAt!))}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Next Steps */}
                <div className="bg-linear-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 sm:p-8 border border-blue-200 mb-6">
                    <h3 className="text-xl font-bold text-blue-900 mb-4">What happens next?</h3>
                    <ul className="space-y-3 text-base text-blue-900">
                        <li className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-blue-200 text-blue-700 font-bold text-sm">1</span>
                            <span>
                                {order.orderState === 'pendingAcceptance' || order.orderState === 'draft'
                                    ? `${locationName} is reviewing your order — you'll be notified once it's confirmed`
                                    : order.orderState === 'scheduled'
                                        ? `We'll start preparing your order shortly before your scheduled time`
                                        : `We're preparing your order right now`}
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-blue-200 text-blue-700 font-bold text-sm">2</span>
                            <span>You'll receive a confirmation email with your order details</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-blue-200 text-blue-700 font-bold text-sm">3</span>
                            <span>We'll notify you when your order is ready</span>
                        </li>
                        {!view.isDelivery && (
                            <li className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-blue-200 text-blue-700 font-bold text-sm">4</span>
                                <span>Come pick up your order at the scheduled time</span>
                            </li>
                        )}
                        {view.isDelivery && (
                            <li className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-blue-200 text-blue-700 font-bold text-sm">4</span>
                                <span>Your order will be delivered to your address</span>
                            </li>
                        )}
                    </ul>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                    {/*
                      What a repeat will not carry over. Listed BEFORE the
                      button, not after the click: a guest who is going to be
                      short a dish should know before they commit, and a price
                      that moved is theirs to accept rather than discover at
                      checkout.
                    */}
                    {reorderPlan?.hasChanges && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                            <p className="font-semibold">Some things have changed since last time</p>
                            <ul className="mt-2 space-y-1">
                                {reorderPlan.lines
                                    .filter((line: ReorderLine) => line.status !== 'available')
                                    .map((line: ReorderLine) => (
                                        <li key={`${line.menuItemId}-${line.status}`}>
                                            {line.status === 'unavailable' && `${line.name} is no longer on the menu`}
                                            {line.status === 'unavailable_now' && `${line.name} isn’t available right now`}
                                            {line.status === 'needs_choice' && `${line.name} needs choosing again`}
                                            {line.status === 'price_changed' && `${line.name} is now $${(line.currentUnitPrice ?? 0).toFixed(2)}`}
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    )}

                    <button
                        onClick={handleReorder}
                        disabled={isReordering}
                        className="w-full text-white font-semibold py-4 rounded-xl hover:opacity-90 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg disabled:opacity-60"
                        style={{
                            background: `linear-gradient(135deg, ${brandColor}dd, ${brandColor})`
                        }}
                    >
                        <span>
                            {isReordering
                                ? 'Adding to your cart…'
                                : reorderPlan?.addable.length
                                  ? `Order Again (${reorderPlan.addable.length} ${reorderPlan.addable.length === 1 ? 'item' : 'items'})`
                                  : `Back to ${locationName}`}
                        </span>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Support Section. This used to be a "Contact Support"
                        button with no handler — a control that did nothing. The
                        guest's help is the restaurant, so it dials the
                        restaurant, and it is absent when there is no number.
                        Neutral text, not the brand colour: a merchant's
                        colour carries no contrast guarantee on this surface. */}
                    {locationRecord?.contact?.phone ? (
                        <div className="text-center py-4">
                            <p className="text-sm text-gray-600">
                                Need help with your order?{' '}
                                <a
                                    href={`tel:${locationRecord.contact.phone}`}
                                    className="inline-flex items-center font-semibold text-gray-900 underline pointer-coarse:min-h-11"
                                >
                                    Call the restaurant
                                </a>
                            </p>
                        </div>
                    ) : null}
                </div>

                {/* Powered by OrderUp */}
                <div className="text-center mt-8 pb-4">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                        <div className="w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">O</span>
                        </div>
                        <span>Powered by OrderUp</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
